import React, { useEffect, useId, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../redux/store";
import BackButton from "../../../components/BackButton";
import Header from "../../../components/Header";
import {
  getManagerApplicationsApplyForTeam,
  setSelectedCourseApplication,
  updateManagerApplicationStatus,
} from "../../../redux/slices/manager.applications.slice";
import SpinnerLoader from "../../../components/SpinnerLoader";
import { FaEllipsisH } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { FaPen } from "react-icons/fa6";
import TabNavbar from "../../../components/Tabbar";
import { MANAGER_TABS } from "../../../utils/TabbarData";
import { downloadDescriptionFile } from "../../../redux/slices/learner.applications.slice";
import { showToast } from "../../../utils/showToast";
import { extractCareerLevelNumber } from "../../../utils/helper";
const STATUS_MAP: any = {
  REJECTED_BY_MANAGER: {
    label: "Denied",
    color: "bg-red-500 text-white",
  },
  APPROVED_BY_MANAGER: {
    label: "Accepted",
    color: "bg-green-500 text-white",
  },
  APPLIED: {
    label: "APPLIED",
    color: "bg-orange-500 text-white",
  },
  APPLICATION_REJECTED_BY_LEARNER: {
    label: "Denied by Learner",
    color: "bg-red-500 text-white",
  },
  HR_APPROVAL_PENDING: {
    label: "HR APPROVAL PENDING",
    color: "bg-orange-500 text-white",
  },
  APPROVED_BY_HR: {
    label: "HR APPROVED",
    color: "bg-green-500 text-white",
  },
};

function ManagerApplications() {
  const { selectedCourseApplication, updateLoading, managerCourses, loading } =
    useSelector((state: RootState) => state.managerApplications) as any;

  const [confirmationMenu, setConfirmationMenu] = useState<{
    action: string;
    item: any;
    isAllow?: boolean;
  } | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null); // Reference for Menu Modal
  const menuMobileRef = useRef<HTMLDivElement | null>(null); // Reference for Menu Modal
  const confirmationRef = useRef<HTMLDivElement | null>(null); // Reference for Confirmation Modal
  const confirmationMobileRef = useRef<HTMLDivElement | null>(null); // Reference for Confirmation Modal
  const [_confirmationMenu, _setConfirmationMenu] = useState<any | null>(null);
  const [selectedApplications, setSelectedApplications] = useState<any[]>([]);
  const [menuOpen, setMenuOpen] = useState<number | null>(null); // Track which menu is open
  const [isLoading, setIsLoading] = useState(false); // Track loading state
  const [isDownloading, setIsDownloading] = useState(false);
  const [dropdownEnabled, setDropdownEnabled] = useState(false); // Dropdown enable state
  const [dropdownValue, setDropdownValue] = useState<any>("SELECT_AN_OPTION");
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const {
    applications,
    course_id,
    course_name,
    course_type,
    training_languages_oc,
    minimum_job_level,
    category_type,
  } = selectedCourseApplication;

  useEffect(() => {
    if (selectedApplications.length > 0) {
      setDropdownEnabled(true); // Enable dropdown when at least one item is selected
    } else {
      setDropdownEnabled(false);
    }
  }, [selectedApplications]);

  const handleDownloadDescriptionFile = async () => {
    setIsDownloading(true);
    try {
      const response = await dispatch(
        downloadDescriptionFile({ course_id: course_id })
      );
      if (response.payload && response.payload.demo_file) {
        const fileUrl = response.payload.demo_file.startsWith('//') 
          ? `https:${response.payload.demo_file}` 
          : response.payload.demo_file;
        window.open(fileUrl, "_blank");
      } else {
        showToast(t("No download material for this course"), "info");
      }
    } finally {
      setIsDownloading(false);
    }
  };

  // Close modals when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        (menuRef.current && menuRef.current.contains(event.target as Node)) ||
        (menuMobileRef.current &&
          menuMobileRef.current.contains(event.target as Node)) ||
        (confirmationRef.current &&
          confirmationRef.current.contains(event.target as Node)) ||
        (confirmationMobileRef.current &&
          confirmationMobileRef.current.contains(event.target as Node))
      ) {
        return; // Don't close if clicking inside a modal or menu
      }

      setMenuOpen(null); // Close the menu modal
      setConfirmationMenu(null); // Close the confirmation modal
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleConfirmation = async (confirm: boolean) => {
    if (confirm && confirmationMenu) {
      const { action, isAllow, item } = confirmationMenu;

      const dataToSend: any = {
        courseId: course_id,
        userId: item.user_id,
      };
      // Example: Call Redux API based on action
      if (action === "APPROVED_BY_MANAGER" && isAllow === true) {
        dataToSend.status = "APPROVED_BY_MANAGER";
        dataToSend.travel = true;
      } else if (action === "APPROVED_BY_MANAGER" && isAllow === false) {
        dataToSend.status = "APPROVED_BY_MANAGER";
        dataToSend.travel = false;
      } else if (action === "REJECTED_BY_MANAGER") {
        dataToSend.status = "REJECTED_BY_MANAGER";
      }

      setConfirmationMenu(null);
      await dispatch(updateManagerApplicationStatus(dataToSend));

      // Step 2: Re-fetch the updated list of manager applications
      const response = await dispatch(
        getManagerApplicationsApplyForTeam({ status: "MANAGER_APPROVAL" })
      );

      // Step 3: Update the selected course application after fetching
      if (response.payload) {
        const updatedCourse = response.payload.data.find(
          (course: any) => course.course_id === course_id
        );

        if (updatedCourse) {
          dispatch(setSelectedCourseApplication(updatedCourse));
        }
      }
    } else {
      setConfirmationMenu(null);
    }
  };

  const handleOptionSelect = (action: string, isAllow: boolean, item: any) => {
    setMenuOpen(null); // Close menu
    setConfirmationMenu({ action, isAllow, item }); // Open confirmation menu
  };

  const handleMenuClick = (index: number) => {
    setMenuOpen(menuOpen === index ? null : index); // Toggle menu open/close
  };

  const handleCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    application: any
  ) => {
    if (e.target.checked) {
      setSelectedApplications((prev) => [...prev, application]);
    } else {
      setSelectedApplications((prev) =>
        prev.filter((item) => item.user_id !== application.user_id)
      );
    }
  };

  const _handleOptionSelect = (action: string) => {
    if (action !== "SELECT_AN_OPTION") {
      _setConfirmationMenu({ action, items: selectedApplications }); // Show confirmation modal with selected items
      setDropdownValue(action);
    }
  };

  const _handleConfirmation = async (confirm: boolean) => {
    if (confirm && _confirmationMenu) {
      const { action, items } = _confirmationMenu;
      setIsLoading(true);

      // Call API for each selected item
      const promises = items.map(async (item: any) => {
        const userId = sessionStorage.getItem("userId") || "";
        const dataToSend: any = {
          courseId: course_id,
          userId: item.user_id,
        };
        if (action === "APPROVED_BY_MANAGER") {
          dataToSend.status = "APPROVED_BY_MANAGER";
          dataToSend.travel = true;
        } else if (action === "APPROVED_BY_MANAGER_NO_ALLOW") {
          dataToSend.status = "APPROVED_BY_MANAGER";
          dataToSend.travel = false;
        } else {
          dataToSend.status = "REJECTED_BY_MANAGER";
        }
        await dispatch(updateManagerApplicationStatus(dataToSend));
      });

      // Wait for all API calls to complete
      await Promise.all(promises);

      // Step 2: Re-fetch the updated list of manager applications
      const response = await dispatch(
        getManagerApplicationsApplyForTeam({ status: "MANAGER_APPROVAL" })
      );

      // Step 3: Update the selected course application after fetching
      if (response.payload) {
        const updatedCourse = response.payload.data.find(
          (course: any) => course.course_id === course_id
        );

        if (updatedCourse) {
          dispatch(setSelectedCourseApplication(updatedCourse));
        }
      }

      setIsLoading(false);
      _setConfirmationMenu(null);
      setSelectedApplications([]); // Clear selected applications
      setDropdownValue("SELECT_AN_OPTION");
    } else {
      _setConfirmationMenu(null); // Close confirmation modal
      setDropdownValue("SELECT_AN_OPTION");
    }
  };

  const isMobileOrTablet = window.innerWidth < 1024; // Checking for mobile/tablet screens

  return (
    <div className="flex flex-col items-start gap-8">
      {isMobileOrTablet ? (
        <>
          {" "}
          <TabNavbar
            id=""
            tabs={MANAGER_TABS}
            selectedTabId={1}
            type="Manager Approvals"
          />
          <BackButton to="/manager" state={{ from: "ManagerCourseSessions" }} />
          <Header />
        </>
      ) : (
        <>
          <BackButton to="/manager" state={{ from: "ManagerCourseSessions" }} />
          <Header />
        </>
      )}

      {isMobileOrTablet && (
        <div className="flex items-center w-full gap-5">
          <button
            className="py-1.5 px-5 bg-white uppercase disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ color: "#961e38", border: "1px solid #961e38" }}
            onClick={handleDownloadDescriptionFile}
            disabled={isDownloading}
          >
            {isDownloading ? t("Downloading...") : t("Download Description")}
          </button>
        </div>
      )}

      <div className="flex flex-row w-full justify-between items-center group cursor-pointer">
        <div className="flex flex-col w-full">
          <p className="text-2xl font-semibold text-[#961e38]">{course_name}</p>
          <div className="flex flex-wrap items-center justify-start gap-5 pt-2">
            <p className="text-xs font-semibold flex items-center justify-start gap-2">
              <span className="text-[#b5b5b5]">{t("Type")}:</span>
              <span>{category_type ? category_type : t("N/A")}</span>
            </p>
            <p className="text-xs font-semibold flex items-center justify-start gap-2">
              <span className="text-[#b5b5b5]">{t("Lang")}:</span>
              <span>
                {training_languages_oc ? training_languages_oc : t("N/A")}
              </span>
            </p>
            <p className="text-xs font-semibold flex items-center justify-start gap-2">
              <span className="text-[#b5b5b5]">{t("Min Career Lvl")}:</span>
              <span>{extractCareerLevelNumber(minimum_job_level)}</span>
            </p>
            {selectedCourseApplication?.classroom_training_cost && (
              <p className="text-xs font-semibold flex items-center justify-start gap-2">
                <span className="text-[#b5b5b5]">
                  {t("Classroom Training Cost")}:
                </span>
                <span>
                  {selectedCourseApplication?.classroom_training_cost}
                </span>
              </p>
            )}
            {selectedCourseApplication?.online_training_cost && (
              <p className="text-xs font-semibold flex items-center justify-start gap-2">
                <span className="text-[#b5b5b5]">
                  {t("Online Training Cost")}:
                </span>
                <span>{selectedCourseApplication?.online_training_cost}</span>
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Mobile/Tablet View */}
      <div className="block md:hidden relative w-full">
        <div className="w-full mb-[4rem]">
          {loading || isLoading ? (
            <div>
              <div className="flex items-center w-full justify-center">
                <SpinnerLoader />
              </div>
              {isLoading && (
                <p className="w-full text-center mt-8">
                  {t("Please do not refresh this page!")}
                </p>
              )}
            </div>
          ) : applications.length > 0 ? (
            applications.map((application: any, i: any) => {
              const { application_status, user_id, first_name } = application;
              return (
                <div
                  key={user_id}
                  className="border border-[#c6c6c6] py-2 text-left bg-[#f3f3f3] mt-2"
                >
                  <div className="px-4 border-b border-[#c6c6c6] pb-2">
                    <div className="flex items-center justify-between w-full">
                      <h3 className="font-[600] pb-2">{t("User")}</h3>
                      <input
                        type="checkbox"
                        onChange={(e) => handleCheckboxChange(e, application)}
                        className="ml-2"
                        disabled={
                          application_status === "APPLIED" ? false : true
                        }
                      />
                    </div>
                    <p className="text-lg">
                      {first_name} {application?.last_name || ""}
                    </p>
                  </div>
                  <div className="flex justify-between items-center px-4 pt-2">
                    <div>
                      <h3 className="font-[600]">{t("Action")}</h3>
                      {application_status !== "APPLIED" && (
                        <div
                          className={`mt-2 p-3 ${
                            STATUS_MAP[application_status]?.color ||
                            "bg-red-500 text-white"
                          }`}
                        >
                          {t(STATUS_MAP[application_status]?.label || "N/A")}
                        </div>
                      )}
                    </div>{" "}
                    {application_status === "APPLIED" && (
                      <button
                        style={{ color: "#961e38 !important" }}
                        onClick={() => {
                          setMenuOpen(null); // Close menu
                          setConfirmationMenu(null);
                          handleMenuClick(i);
                        }}
                        className="text-gray-500 hover:text-gray-700 focus:outline-none"
                      >
                        <FaPen color="#961e38" size={20} />
                      </button>
                    )}
                  </div>

                  {/* Conditional Menu */}
                  {menuOpen === i && (
                    <div
                      ref={menuMobileRef} // Attach the ref to the menu modal
                      className="absolute z-10 bg-white shadow-md border rounded mt-2"
                      style={{ right: "2rem" }}
                    >
                      <ul>
                        <li
                          onClick={() =>
                            handleOptionSelect(
                              "APPROVED_BY_MANAGER",
                              true,
                              application
                            )
                          }
                          className="flex justify-center px-4 py-2 hover:bg-gray-100 cursor-pointer"
                        >
                          {t("Approve For Travel")}
                        </li>
                        <li
                          onClick={() =>
                            handleOptionSelect(
                              "APPROVED_BY_MANAGER",
                              false,
                              application
                            )
                          }
                          className="flex justify-center px-4 py-2 hover:bg-gray-100 cursor-pointer"
                        >
                          {t("Approve For No Travel")}
                        </li>
                        <li
                          onClick={() =>
                            handleOptionSelect(
                              "REJECTED_BY_MANAGER",
                              false,
                              application
                            )
                          }
                          className="flex justify-center px-4 py-2 hover:bg-gray-100 cursor-pointer"
                        >
                          {t("Deny")}
                        </li>
                      </ul>
                    </div>
                  )}

                  {confirmationMenu &&
                    confirmationMenu.item === application && (
                      <div
                        ref={confirmationMobileRef}
                        className="absolute z-20 bg-white shadow-md border rounded"
                        style={{ right: "2rem" }}
                      >
                        <p className="px-4 py-2">{t("Are you sure?")}</p>
                        <div className="flex justify-around px-4 py-2">
                          <button
                            onClick={() => handleConfirmation(true)}
                            className="text-green-500 hover:underline"
                          >
                            {t("Yes")}
                          </button>

                          <button
                            onClick={() => handleConfirmation(false)}
                            className="text-red-500 hover:underline"
                          >
                            {t("No")}
                          </button>
                        </div>
                      </div>
                    )}

                  {_confirmationMenu && (
                    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
                      <div className="bg-white p-5 rounded-md shadow-lg flex flex-col justify-between items-end w-[90%] sm:w-[500px] h-[auto] max-h-[80vh]">
                        <div className="flex flex-col items-start justify-start gap-5 w-full">
                          <h2 className="text-lg font-semibold text-red-600">
                            {t("Confirm Action")}
                          </h2>
                          <p className="mt-2 text-left">
                            {t(
                              `Are you sure want to ${
                                _confirmationMenu.action ===
                                "APPROVED_BY_MANAGER"
                                  ? "approve for travel"
                                  : _confirmationMenu.action ===
                                    "APPROVED_BY_MANAGER_NO_ALLOW"
                                  ? "approve for no travel"
                                  : "deny"
                              } these ${
                                _confirmationMenu.items.length
                              } applications?`
                            )}
                          </p>
                        </div>
                        <div className="mt-4 flex justify-end gap-2">
                          <button
                            className="px-4 py-2 bg-blue-500 text-white rounded"
                            onClick={() => _handleConfirmation(true)}
                          >
                            {t("Yes")}
                          </button>
                          <button
                            className="px-4 py-2 bg-red-500 text-white rounded"
                            onClick={() => _handleConfirmation(false)}
                          >
                            {t("No")}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <p className="w-full text-center py-5">{t("No Data Available")}</p>
          )}
        </div>
        <div className="fixed bottom-0 left-0 w-full p-4 bg-white">
          <div className="flex justify-between items-center ml-2">
            <select
              className="p-2 border"
              disabled={!dropdownEnabled} // Disable dropdown if no items are selected
              onChange={(e) => _handleOptionSelect(e.target.value)}
              value={dropdownValue}
            >
              <option value="SELECT_AN_OPTION" disabled selected>
                {t("Select a Bulk Action")}
              </option>
              <option value="APPROVED_BY_MANAGER">
                {t("Approve For Travel")}
              </option>
              <option value="APPROVED_BY_MANAGER_NO_ALLOW">
                {t("Approve For No Travel")}
              </option>
              <option value="REJECTED_BY_MANAGER">{t("Deny")}</option>
            </select>
          </div>
        </div>
      </div>
      {/* Desktop View */}
      <div className="hidden md:block w-full h-full">
        <div className="flex w-full items-center justify-between mb-4">
          <div className="flex items-center w-full gap-5">
            <button
              className="py-1.5 px-5 bg-white uppercase disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ color: "#961e38", border: "1px solid #961e38" }}
              onClick={handleDownloadDescriptionFile}
              disabled={isDownloading}
            >
              {isDownloading ? t("Downloading...") : t("Download Description")}
            </button>
          </div>
          <select
            className="p-2 border"
            disabled={!dropdownEnabled} // Disable dropdown if no items are selected
            onChange={(e) => _handleOptionSelect(e.target.value)}
            value={dropdownValue}
          >
            <option value="SELECT_AN_OPTION" disabled selected>
              {t("Select a Bulk Action")}
            </option>
            <option value="APPROVED_BY_MANAGER">
              {t("Approve For Travel")}
            </option>
            <option value="APPROVED_BY_MANAGER_NO_ALLOW">
              {t("Approve For No Travel")}
            </option>
            <option value="REJECTED_BY_MANAGER">{t("Deny")}</option>
          </select>
        </div>
        <table className="w-full border-collapse">
          <thead className="bg-[#961e38] w-full text-white rounded-t-xl">
            <tr>
              <th className="py-2 px-4 text-center">{t("Select")}</th>
              <th className="py-2 px-4 text-center">{t("Users")}</th>
              <th className="py-2 px-4 text-center">{t("Action")}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={2} className="py-2 px-4 text-center">
                  <SpinnerLoader />
                  {isLoading && (
                    <p className="w-full text-center mt-8">
                      {t("Please do not refresh this page!")}
                    </p>
                  )}
                </td>
              </tr>
            ) : applications.length > 0 ? (
              applications.map((application: any, i: any) => {
                const { application_status, user_id, first_name } = application;
                return (
                  <tr key={user_id}>
                    <td className="border-b border-[#c6c6c6] py-2 px-4 text-center">
                      <input
                        type="checkbox"
                        onChange={(e) => handleCheckboxChange(e, application)}
                        disabled={
                          application_status === "APPLIED" ? false : true
                        }
                      />
                    </td>
                    <td className="border-b border-[#c6c6c6] py-2 px-4 text-center">
                      {first_name} {application?.last_name || ""}
                    </td>
                    <td
                      className={`border-b border-[#c6c6c6] py-2 px-4 text-center`}
                    >
                      {application_status === "APPLIED" ? (
                        <div className="flex items-center justify-center gap-3">
                          <button
                            onClick={() => {
                              setMenuOpen(null); // Close menu
                              setConfirmationMenu(null);
                              handleMenuClick(i);
                            }}
                            className="text-gray-500 hover:text-gray-700 focus:outline-none"
                            title={t("More options")}
                          >
                            <FaEllipsisH size={20} />
                          </button>
                        </div>
                      ) : (
                        <div
                          className={`flex items-center justify-center mt-2 p-3 ${
                            STATUS_MAP[application_status]?.color ||
                            "bg-red-500 text-white"
                          }`}
                        >
                          {t(STATUS_MAP[application_status]?.label || "N/A")}
                        </div>
                      )}

                      {/* Conditional Menu */}
                      {menuOpen === i && (
                        <div
                          ref={menuRef} // Attach the ref to the menu modal
                          className="absolute z-10 bg-white shadow-md border rounded"
                          style={{ right: "24rem" }}
                        >
                          <ul>
                            {
                              <li
                                onClick={() =>
                                  handleOptionSelect(
                                    "APPROVED_BY_MANAGER",
                                    true,
                                    application
                                  )
                                }
                                className="flex justify-center px-4 py-2 hover:bg-gray-100 cursor-pointer"
                              >
                                {t("Approve For Travel")}
                              </li>
                            }
                            {
                              <li
                                onClick={() =>
                                  handleOptionSelect(
                                    "APPROVED_BY_MANAGER",
                                    false,
                                    application
                                  )
                                }
                                className="flex justify-center px-4 py-2 hover:bg-gray-100 cursor-pointer"
                              >
                                {t("Approve For No Travel")}
                              </li>
                            }
                            {
                              <li
                                onClick={() =>
                                  handleOptionSelect(
                                    "REJECTED_BY_MANAGER",
                                    false,
                                    application
                                  )
                                }
                                className="flex justify-center px-4 py-2 hover:bg-gray-100 cursor-pointer"
                              >
                                {t("Deny")}
                              </li>
                            }
                          </ul>
                        </div>
                      )}
                      {/* Confirmation Menu */}
                      {confirmationMenu &&
                        confirmationMenu.item === application && (
                          <div
                            ref={confirmationRef}
                            className="absolute z-20 bg-white shadow-md border rounded"
                            style={{ right: "22rem" }}
                          >
                            <p className="px-4 py-2">{t("Are you sure?")}</p>
                            <div className="flex justify-around px-4 py-2">
                              <button
                                onClick={() => handleConfirmation(true)}
                                className="text-green-500 hover:underline"
                              >
                                {t("Yes")}
                              </button>

                              <button
                                onClick={() => handleConfirmation(false)}
                                className="text-red-500 hover:underline"
                              >
                                {t("No")}
                              </button>
                            </div>
                          </div>
                        )}
                      {/* Confirmation Menu */}
                      {_confirmationMenu && (
                        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
                          <div className="bg-white p-5 rounded-md shadow-lg flex flex-col justify-between items-end w-[90%] sm:w-[500px] h-[auto] max-h-[80vh]">
                            <div className="flex flex-col items-start justify-start gap-5 w-full">
                              <h2 className="text-lg font-semibold text-red-600">
                                {t("Confirm Action")}
                              </h2>
                              <p className="mt-2 text-left">
                                {t(
                                  `Are you sure want to ${
                                    _confirmationMenu.action ===
                                    "APPROVED_BY_MANAGER"
                                      ? "approve for travel"
                                      : _confirmationMenu.action ===
                                        "APPROVED_BY_MANAGER_NO_ALLOW"
                                      ? "approve for no travel"
                                      : "deny"
                                  } these ${
                                    _confirmationMenu.items.length
                                  } applications?`
                                )}
                              </p>
                            </div>
                            <div className="mt-4 flex justify-end gap-2">
                              <button
                                className="px-4 py-2 bg-blue-500 text-white rounded"
                                onClick={() => _handleConfirmation(true)}
                              >
                                {t("Yes")}
                              </button>
                              <button
                                className="px-4 py-2 bg-red-500 text-white rounded"
                                onClick={() => _handleConfirmation(false)}
                              >
                                {t("No")}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={2}>
                  <p className="w-full text-center py-5">
                    {t("No Data Available")}
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ManagerApplications;

{
  /* <tfoot className="bg-[#961e38]">
            <tr>
              <td colSpan={2} className="p-5 text-white rounded-b-xl">
                <div className="flex items-center justify-between rounded-b-xl">
                  <div className="flex items-center gap-2">
                    <p className="text-sm">Min Seats</p>
                    <input
                      className="w-12 text-center bg-white text-black"
                      value={minMaxEnroll.min}
                      onChange={(e) => handleMinChange(Number(e.target.value))}
                      disabled={sessionStatus !== "AVAILABLE"}
                    />
                    <p className="text-sm">Max Seats</p>
                    <input
                      className="w-12 text-center bg-white text-black"
                      value={minMaxEnroll.max}
                      onChange={(e) => handleMaxChange(Number(e.target.value))}
                      disabled={sessionStatus !== "AVAILABLE"}
                    />
                  </div>
                  {sessionStatus === "AVAILABLE" && (
                    <button
                      className="py-1.5 px-5 border border-white"
                      onClick={handleMinMaxSeatsSave}
                      disabled={updateLoading}
                    >
                      {updateLoading ? `${t("Saving")}...` : t("Save")}
                    </button>
                  )}
                </div>
              </td>
            </tr>
          </tfoot> */
}
